import { useEffect, useState } from 'react';

const fetchJson = async (url, options = {}) => {
    const response = await fetch(url, {
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        ...options,
    });

    if (!response.ok) {
        const err = await response.json().catch(() => null);
        throw new Error(err?.message || `${response.status} ${response.statusText}`);
    }

    return response.json();
};

export default function ProductTypesTab({ productTypes, onSaveProductType, onDeleteProductType }) {
    const [editingType, setEditingType] = useState(null);
    const [form, setForm] = useState({ name: '', description: '', is_active: true });

    useEffect(() => {
        if (!editingType) {
            setForm({ name: '', description: '', is_active: true });
            return;
        }

        const type = productTypes.find((item) => item.id === editingType);
        if (type) {
            setForm({
                name: type.name,
                description: type.description || '',
                is_active: type.is_active,
            });
        }
    }, [editingType, productTypes]);

    const handleSubmit = (event) => {
        event.preventDefault();
        const trimmedName = form.name.trim();
        if (!trimmedName) return;

        onSaveProductType({ id: editingType, ...form });
        setEditingType(null);
        setForm({ name: '', description: '', is_active: true });
    };

    return (
        <div className="space-y-6">
            <section className="rounded-3xl border border-indigo-400 bg-indigo-50/90 p-5 shadow-xl shadow-slate-950/20">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <p className="text-sm uppercase tracking-[0.35em] text-indigo-400">Types de produit</p>
                        <h2 className="text-3xl font-semibold text-indigo-900">Gestion des types de produit</h2>
                    </div>
                    <p className="max-w-xl text-sm text-indigo-600">
                        Créez et administrez les types de produit utilisés dans votre gestion de stock.
                    </p>
                </div>
            </section>

            <div className="grid gap-6 lg:grid-cols-[1.2fr_0.8fr]">
                <div className="space-y-4">
                    {productTypes.map((type) => (
                        <div key={type.id} className="rounded-3xl border border-indigo-400 bg-white p-5 shadow-xl shadow-slate-950/20">
                            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                <div>
                                    <p className="text-lg font-semibold text-indigo-900">{type.name}</p>
                                    <p className="text-sm text-indigo-600">{type.description || 'Aucune description'}</p>
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => setEditingType(type.id)}
                                        className="rounded-3xl border border-indigo-300 bg-indigo-50 px-4 py-2 text-sm text-indigo-800 hover:border-indigo-500"
                                    >
                                        Éditer
                                    </button>
                                    <button
                                        onClick={() => onDeleteProductType(type.id)}
                                        className="rounded-3xl border border-rose-500 bg-rose-100 px-4 py-2 text-sm text-rose-500 hover:bg-rose-200"
                                    >
                                        Supprimer
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                    {!productTypes.length && (
                        <div className="rounded-3xl border border-indigo-400 bg-white p-5 text-indigo-700 shadow-xl shadow-slate-950/20">
                            Aucun type de produit enregistré.
                        </div>
                    )}
                </div>

                <div className="rounded-3xl border border-indigo-400 bg-white p-5 shadow-xl shadow-slate-950/20">
                    <h3 className="mb-4 text-xl font-semibold text-indigo-900">
                        {editingType ? 'Modifier un type' : 'Ajouter un type'}
                    </h3>
                    <form className="space-y-4" onSubmit={handleSubmit}>
                        <div>
                            <label className="text-sm text-indigo-600">Nom du type</label>
                            <input
                                value={form.name}
                                onChange={(event) => setForm({ ...form, name: event.target.value })}
                                className="mt-2 w-full rounded-3xl border border-indigo-400 bg-indigo-50 px-4 py-3 text-indigo-900 focus:border-indigo-500 focus:outline-none"
                                placeholder="Ex. Boisson, Entretien, Emballage"
                            />
                        </div>
                        <div>
                            <label className="text-sm text-indigo-600">Description</label>
                            <textarea
                                value={form.description}
                                onChange={(event) => setForm({ ...form, description: event.target.value })}
                                className="mt-2 w-full rounded-3xl border border-indigo-400 bg-indigo-50 px-4 py-3 text-indigo-900 focus:border-indigo-500 focus:outline-none"
                                rows={4}
                                placeholder="Description du type"
                            />
                        </div>
                        <label className="flex items-center gap-2 text-sm text-indigo-600">
                            <input
                                type="checkbox"
                                checked={form.is_active}
                                onChange={(event) => setForm({ ...form, is_active: event.target.checked })}
                                className="h-4 w-4 rounded border-indigo-300 text-indigo-600"
                            />
                            Actif
                        </label>
                        <div className="flex gap-3">
                            <button
                                type="submit"
                                className="rounded-3xl bg-indigo-600 px-5 py-3 font-semibold text-white hover:bg-indigo-500"
                            >
                                Enregistrer
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setEditingType(null);
                                    setForm({ name: '', description: '', is_active: true });
                                }}
                                className="rounded-3xl border border-indigo-300 bg-indigo-50 px-5 py-3 font-semibold text-indigo-700 hover:bg-indigo-100"
                            >
                                Annuler
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}
